import "@/App.css";
import "@/v2.css";
import "@/v14.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Nav } from "./components/site/Nav";
import { Footer } from "./components/site/Footer";
import { getLenis, ScrollTrigger, scrollToTop, startSmoothScroll, useReveal } from "./lib/motion";
import Home from "./pages/Home";

const IaAutomatisation = lazy(() => import("./pages/IaAutomatisation"));
const Integrations = lazy(() => import("./pages/Integrations"));
const About = lazy(() => import("./pages/About"));
const named = (loader, name) => lazy(() => loader().then((m) => ({ default: m[name] })));
const misc = () => import("./pages/Misc");
const legal = () => import("./pages/Legal");
const commerce = () => import("./pages/Commerce");
const Contact = named(misc, "Contact");
const NotFound = named(misc, "NotFound");
const MentionsLegales = named(legal, "MentionsLegales");
const Confidentialite = named(legal, "Confidentialite");
const ApercuBoutique = named(commerce, "ApercuBoutique");
const SitesMaintenance = lazy(() => import("./pages/SitesMaintenance"));
const ApercuLivraison = lazy(() => import("./pages/ApercuLivraison"));
const REVIEW_PAGE_ENABLED = false;
const AdminGate = lazy(() => import(/* webpackChunkName: "admin-gate" */ "./admin/AdminGate"));
const AcceptQuote = lazy(() => import(/* webpackChunkName: "devis-accept" */ "./pages/AcceptQuote"));
const Portal = lazy(() => import(/* webpackChunkName: "portail-client" */ "./pages/Portal"));
const StrategieIa = lazy(() => import("./pages/StrategieIa"));

function Layout() {
  const { pathname, hash, search } = useLocation();
  const navigate = useNavigate();

  useEffect(() => { startSmoothScroll(); }, []);
  useEffect(() => {
    if (!pathname.endsWith("/")) navigate(`${pathname}/${hash}`, { replace: true });
  }, [pathname, hash, navigate]);
  useEffect(() => {
    if (!hash) scrollToTop();
    let tries = 0;
    let id = 0;
    const go = () => {
      ScrollTrigger.refresh();
      const el = hash && document.getElementById(hash.slice(1));
      if (!el) { if (hash && tries++ < 40) id = setTimeout(go, 50); return; }
      const lenis = getLenis();
      if (lenis) lenis.scrollTo(el, { offset: -90 });
      else el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    id = setTimeout(go, 0);
    return () => clearTimeout(id);
  }, [pathname, hash, search]);
  useReveal();

  return (
    <>
      <a href="#main" className="skip-link">Aller au contenu</a>
      <div className="grain" aria-hidden="true" />
      <Nav />
      <main id="main">
        <Suspense fallback={<div className="page-loading" aria-busy="true" />}>
          <Outlet />
        </Suspense>
      </main>
      <Footer />
    </>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/admin/*" element={<Suspense fallback={<div className="adm-boot" />}><AdminGate /></Suspense>} />
          <Route path="/devis/:token" element={<Suspense fallback={<div className="adm-boot" />}><AcceptQuote /></Suspense>} />
          <Route path="/portail/:token" element={<Suspense fallback={<div className="adm-boot" />}><Portal /></Suspense>} />
          <Route path="/portail/:token/devis/:qid" element={<Suspense fallback={<div className="adm-boot" />}><AcceptQuote /></Suspense>} />
          <Route element={<Layout />}>
            <Route path="/" element={<Home />} />
            <Route path="/sites-internet-maintenance/" element={<SitesMaintenance />} />
            <Route path="/ia-automatisation/" element={<IaAutomatisation />} />
            <Route path="/strategie-ia/" element={<StrategieIa />} />
            <Route path="/integrations-api/" element={<Integrations />} />
            <Route path="/realisations/" element={<Navigate to="/" replace />} />
            <Route path="/a-propos/" element={<About />} />
            <Route path="/contact/" element={<Contact />} />
            <Route path="/apercu-boutique/" element={<ApercuBoutique />} />
            <Route path="/mentions-legales/" element={<MentionsLegales />} />
            <Route path="/confidentialite/" element={<Confidentialite />} />
            {REVIEW_PAGE_ENABLED && <Route path="/apercu-livraison/" element={<ApercuLivraison />} />}
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </HelmetProvider>
  );
}
